import type { BadgeTone } from '../ui/primitives';
import type { ComplaintEventResponse, ComplaintResponse, ComplaintSeverity, ComplaintStatus } from '../api/types';

/**
 * The complaint rules both apps have to agree on.
 *
 * Pure functions with no React in them, for the same reason `AbsenceRules` and `Billing` are pure functions
 * on the server: the guardian app and the admin console render the same record and must not disagree about
 * what it says. None of this is a security boundary — every one of these questions is answered again by the
 * API, which is where the answer actually binds.
 */

/** The colour a status carries. Withdrawn and closed are neutral: they are endings, not outcomes. */
export function complaintTone(status: ComplaintStatus): BadgeTone {
  switch (status) {
    case 'Submitted': return 'warning';
    case 'Acknowledged': return 'info';
    case 'Assigned':
    case 'InProgress': return 'brand';
    case 'Resolved': return 'success';
    case 'Closed':
    case 'Withdrawn': return 'neutral';
    default: return 'neutral';
  }
}

export function severityTone(severity: ComplaintSeverity): BadgeTone {
  switch (severity) {
    case 'High': return 'danger';
    case 'Low': return 'neutral';
    default: return 'info';
  }
}

/** Whether a complaint is still being worked. Mirrors `ComplaintWorkflow.IsOpen`. */
export function isOpen(status: ComplaintStatus): boolean {
  return status === 'Submitted' || status === 'Acknowledged'
      || status === 'Assigned' || status === 'InProgress';
}

/**
 * Whether the setting is past its own acknowledgement target.
 *
 * A display cue only — nothing escalates on it (Q-CMP-3). A complaint that has been acknowledged is never
 * late, however long it took, because the target is about the first response rather than the outcome.
 */
export function isOverdue(complaint: ComplaintResponse, now: Date = new Date()): boolean {
  if (complaint.acknowledgedAt != null) return false;
  if (!isOpen(complaint.status)) return false;
  return new Date(complaint.acknowledgementDueAt).getTime() < now.getTime();
}

/**
 * Whether a timeline entry is handling discussion rather than correspondence.
 *
 * The API never sends an internal entry to a guardian, so this decides styling and not access. It is a
 * function rather than a bare `event.internal` read because the console leans on it in several places and
 * the one that matters — telling a note apart from a reply before an administrator presses send — should
 * read the same everywhere.
 */
export function isInternal(event: ComplaintEventResponse): boolean {
  return event.internal
      || event.eventType === 'InternalNote'
      || event.eventType === 'SeverityChanged'
      || event.eventType === 'StatusChanged'
      || event.eventType === 'Assigned'
      || event.eventType === 'Reassigned';
}

/** Whether an entry is something someone wrote, as opposed to something that happened. */
export function isMessage(event: ComplaintEventResponse): boolean {
  return event.eventType === 'InternalNote'
      || event.eventType === 'ReplyToGuardian'
      || event.eventType === 'GuardianReply';
}
